import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useAccessTheme } from './useAccessTheme';

interface ChangePasswordModalProps {
  onClose: () => void;
  onSubmit: (newPassword: string) => void;
  email: string;
}

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({ onClose, onSubmit, email }) => {
  const t = useAccessTheme();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = () => {
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long.');
      return;
    }
    if (!new RegExp('[A-Z]').test(newPassword)) {
      setError('Password must contain at least one uppercase letter.');
      return;
    }
    if (!new RegExp('[a-z]').test(newPassword)) {
      setError('Password must contain at least one lowercase letter.');
      return;
    }
    if (!new RegExp('[0-9]').test(newPassword)) {
      setError('Password must contain at least one number.');
      return;
    }
    if (!new RegExp('[!@#$%^&*]').test(newPassword)) {
      setError('Password must include a special character (!@#$%^&*).');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    onSubmit(newPassword);
    onClose();
  };

  const labelCls = 'block text-xs font-medium mb-1 ' + t.muted;
  const inputCls = 'w-full border rounded-lg px-3 py-2 text-sm outline-none ' + t.input;

  return createPortal(
    <div
      className={'fixed inset-0 flex items-center justify-center p-4 ' + t.overlay}
      style={{ zIndex: 200 }}
    >
      <div className={'rounded-xl shadow-xl w-full max-w-sm overflow-hidden border ' + t.panel}>
        <div className={'flex items-center justify-between px-5 py-4 border-b ' + t.header}>
          <h3 className={'text-base font-semibold ' + t.title}>Update Password</h3>
          <button type="button" onClick={onClose} className={'p-1.5 rounded-lg ' + t.closeBtn}>
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className={'rounded-lg border px-3 py-2 ' + t.border}>
            <p className={'text-xs ' + t.muted}>Account</p>
            <p className="text-sm font-medium truncate">{email}</p>
          </div>

          <div>
            <label className={labelCls}>New Password</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password"
              className={inputCls}
            />
          </div>

          <div>
            <label className={labelCls}>Confirm Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Repeat new password"
              className={inputCls}
            />
          </div>

          {error ? (
            <div className={'border rounded-lg px-3 py-2 text-xs ' + t.errorBox}>{error}</div>
          ) : null}
        </div>

        <div className={'px-5 py-4 border-t flex justify-end gap-2 ' + t.footer}>
          <button
            type="button"
            onClick={onClose}
            className={'px-4 py-2 rounded-lg text-sm font-medium ' + t.btnGhost}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className={'px-4 py-2 rounded-lg text-sm font-medium ' + t.btnPrimary}
          >
            Update Password
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ChangePasswordModal;
